/**
 * StatsSection - User progress and statistics
 */
import { Progress } from "@/components/ui/progress";
import { MapPin, Compass, Star, Target, Heart } from "lucide-react";

const CATEGORIES = [
  { value: "travel", label: "Travel", icon: MapPin, color: "bg-primary/10 text-primary" },
  { value: "adventures", label: "Adventures", icon: Compass, color: "bg-secondary/10 text-secondary" },
  { value: "experiences", label: "Experiences", icon: Star, color: "bg-accent/30 text-foreground" },
  { value: "goals", label: "Goals", icon: Target, color: "bg-charcoal/10 text-charcoal" },
  { value: "romance", label: "Romance", icon: Heart, color: "bg-coral/10 text-coral" },
];

const StatsSection = ({ stats }) => {
  if (!stats) return null;

  const progress = stats.total_items > 0 
    ? Math.round((stats.completed_items / stats.total_items) * 100) 
    : 0;

  return (
    <section 
      className="bg-card rounded-2xl border border-charcoal/5 shadow-card overflow-hidden animate-fade-in" 
      style={{ animationDelay: "0.1s" }}
    >
      <div className="p-6 border-b border-charcoal/5">
        <h2 className="font-serif text-xl font-medium text-charcoal">Your Progress</h2>
      </div>

      <div className="p-6 space-y-6">
        {/* Overall Progress */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-32 h-32 relative">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#EAE7D6"
                strokeWidth="8"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#E07A5F"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${progress * 2.51} 251`}
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-charcoal">{progress}%</span>
              <span className="text-xs text-muted-foreground">Complete</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-primary/5 rounded-xl">
            <p className="text-3xl font-bold text-primary">{stats.total_items}</p>
            <p className="text-sm text-muted-foreground">Total Items</p>
          </div>
          <div className="text-center p-4 bg-secondary/5 rounded-xl">
            <p className="text-3xl font-bold text-secondary">{stats.completed_items}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </div>
        </div>

        {/* Category Breakdown */}
        {stats.by_category && Object.keys(stats.by_category).length > 0 && (
          <div className="space-y-3">
            <h3 className="font-medium text-charcoal text-sm">By Category</h3>
            {CATEGORIES.map((cat) => {
              const catStats = stats.by_category[cat.value];
              if (!catStats) return null;
              const catProgress = catStats.total > 0 
                ? Math.round((catStats.completed / catStats.total) * 100) 
                : 0;
              const Icon = cat.icon;
              
              return (
                <div key={cat.value} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full ${cat.color} flex items-center justify-center`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <span className="text-sm text-charcoal">{cat.label}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {catStats.completed}/{catStats.total}
                    </span>
                  </div>
                  <Progress value={catProgress} className="h-1.5" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default StatsSection;
